import React from "react";
import { Instagram, Phone } from "lucide-react";
import {
  PROFILE_FIELD_IDS,
  scrollToFirstProfileFieldError,
} from "../../lib/profileForm";
import { validateOptionalContactFields } from "../../lib/contactValidation";
import { Button } from "../ui";
import Card from "../ui/Card";
import FormTextField from "../FormTextField";
import FormLabel from "../FormLabel";
import ContactPhoneField from "../ContactPhoneField";
import InstagramInput from "../InstagramInput";
import {
  FieldHint,
  FORM_STYLE_CARD_CLASS,
  FormValidationAlert,
  SectionTitle,
} from "../createEvent/CreateEventFormUi";
import HostOnboardingStickyActions, {
  ONBOARDING_DESKTOP_ACTIONS,
} from "./HostOnboardingStickyActions";

const FORM_ID = "host-onboarding-profile-form";
const FIELD_ORDER = ["name", "contact_phone"];

/**
 * Host onboarding — name + optional contact. Primary CTA in thumb zone on mobile.
 */
export default function HostOnboardingProfileStep({
  name,
  onNameChange,
  contactPhone,
  onContactPhoneChange,
  personalInstagram,
  onPersonalInstagramChange,
  fieldErrors,
  onSetFieldErrors,
  onClearFieldError,
  saving,
  onSave,
}) {
  function handleSubmit(e) {
    e.preventDefault();
    const errors = {};
    if (!name?.trim()) errors.name = "Enter your name.";
    Object.assign(errors, validateOptionalContactFields({ phone: contactPhone }));
    if (Object.keys(errors).length > 0) {
      onSetFieldErrors(errors);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToFirstProfileFieldError(errors, FIELD_ORDER));
      });
      return;
    }
    onSetFieldErrors({});
    onSave(e);
  }

  const continueLabel = saving ? "Saving…" : "Continue →";

  return (
    <>
      <form
        id={FORM_ID}
        onSubmit={handleSubmit}
        className="space-y-5"
        noValidate
        aria-busy={saving}
        data-testid="host-onboarding-profile-form"
      >
        <FormValidationAlert count={Object.keys(fieldErrors).length} />

        <Card className={FORM_STYLE_CARD_CLASS} data-testid="host-onboarding-about">
          <FormTextField
            id={PROFILE_FIELD_IDS.name}
            label="Name"
            required
            error={fieldErrors.name}
            value={name}
            onChange={(e) => {
              onClearFieldError("name");
              onNameChange(e.target.value);
            }}
            placeholder="Marta"
            autoComplete="name"
            maxLength={100}
            data-testid="host-onboarding-name-input"
          />
          <FieldHint className="mt-2 text-[13px]">
            Guests see this on your events and host page.
          </FieldHint>

          <div className="mt-6 pt-5 border-t border-black/[0.08]">
            <SectionTitle icon={Phone}>Contact</SectionTitle>
            <FieldHint className="text-[13px]">
              Optional — for last-minute guest questions.
            </FieldHint>
            <ContactPhoneField
              className="mt-4"
              id={PROFILE_FIELD_IDS.contact_phone}
              value={contactPhone}
              onChange={(v) => {
                onClearFieldError("contact_phone");
                onContactPhoneChange(v);
              }}
              error={fieldErrors.contact_phone}
              errorId={`${PROFILE_FIELD_IDS.contact_phone}-error`}
              hint="WhatsApp number works best."
              testId="host-onboarding-phone-input"
            />
            <label className="block mt-4">
              <FormLabel className="block mb-2 flex items-center gap-1.5">
                <Instagram className="w-3.5 h-3.5 shrink-0" strokeWidth={2} aria-hidden />
                Instagram{" "}
                <span className="font-normal text-ink/55">(optional)</span>
              </FormLabel>
              <InstagramInput
                id="host-onboarding-instagram-input"
                value={personalInstagram}
                onChange={onPersonalInstagramChange}
                placeholder="yourhandle"
                data-testid="host-onboarding-instagram-input"
              />
            </label>
          </div>
        </Card>

        <div className={ONBOARDING_DESKTOP_ACTIONS}>
          <span className="flex-1" aria-hidden />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={saving}
            data-testid="host-onboarding-profile-save"
            className="min-w-[10rem]"
          >
            {continueLabel}
          </Button>
        </div>
      </form>

      <HostOnboardingStickyActions testId="host-onboarding-profile-sticky">
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          size="lg"
          disabled={saving}
          className="flex-1"
          data-testid="host-onboarding-profile-save-sticky"
        >
          {continueLabel}
        </Button>
      </HostOnboardingStickyActions>
    </>
  );
}
